import { StateList } from '@120wateraudit/envirio-components';
import {
  Account,
  AccountType
} from '@120wateraudit/envirio-components/dist/models';
import { ValidationErrors } from 'final-form';
import omit from 'lodash/omit';
import React from 'react';
import { Field, useField } from 'react-final-form';
import { useFormState } from 'react-final-form';
import { Divider, Form, Icon, Input, Label } from 'semantic-ui-react';
import styled from 'styled-components';

import Dropdown from 'src/components/Dropdown/Dropdown';
import TextField from 'src/components/TextField/TextField';
import {
  ownerOptions,
  pwsTypeOptions,
  timezoneOptions
} from 'src/utils/SelectList';
import {
  isEmptyString,
  isValidZip,
  isValidSubdomain
} from 'src/utils/validation';

export interface Values {
  accountType?: AccountType | 'pws' | 'schools' | 'state' | 'hydra';
  city?: string;
  companyName?: string;
  connection?: string;
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
  isAccountDisabled?: boolean;
  isDemo?: boolean;
  latitude?: number | string;
  longitude?: number | string;
  name?: string;
  owner?: string;
  populationServedCount?: number | string;
  primacyCode?: string;
  pwsType?: string;
  pwsid?: string;
  sdwisContact?: string;
  serviceLineConnectionsCount?: number | string;
  state?: string;
  step: 'info' | 'type';
  streetAddressLine1?: string;
  streetAddressLine2?: string | null;
  subdomain?: string | null;
  timezone?: string;
  zip?: string;
}

const SDWIS_ID_REGEX = /^[a-zA-Z0-9]+$/;

export type AccountWithTimezone = Account & {
  owner?: string;
  populationServedCount?: number;
  primacyCode?: string;
  pwsType?: string;
  pwsid?: string;
  sdwisContact?: string;
  serviceLineConnectionsCount?: number;
  timezone?: string;
};

export const validate = (values: Values): ValidationErrors => {
  const errors: ValidationErrors = {};
  if (isEmptyString(values.name)) {
    errors.name = 'Name is Required';
  }

  if (isEmptyString(values.companyName)) {
    errors.companyName = 'Company Name is Required';
  }

  if (isEmptyString(values.streetAddressLine1)) {
    errors.streetAddressLine1 = 'Address Line 1 is Required';
  }

  if (isEmptyString(values.city)) {
    errors.city = 'City is Required';
  }

  if (isEmptyString(values.state)) {
    errors.state = 'State is Required';
  }

  if (!values.zip || isEmptyString(values.zip)) {
    errors.zip = 'ZIP is Required';
  } else if (!isValidZip(values.zip)) {
    errors.zip = 'ZIP must be a valid postal code';
  }

  if (values.subdomain && !isValidSubdomain(values.subdomain)) {
    errors.subdomain =
      'Subdomain must be a only letters, underscores, and dashes';
  }

  if (values.subdomain && values.subdomain.length > 50) {
    errors.subdomain = 'Subdomain must be a maximum of 50 characters';
  }

  if (isEmptyString(values.contactName)) {
    errors.contactName = 'Contact Name is Required';
  }

  // Either contactPhone or contactEmail is required, but not both
  const hasPhone = values.contactPhone && !isEmptyString(values.contactPhone);
  const hasEmail = values.contactEmail && !isEmptyString(values.contactEmail);
  if (!hasPhone && !hasEmail) {
    errors.contactPhone = 'Either Contact Phone or Contact Email is required';
    errors.contactEmail = 'Either Contact Phone or Contact Email is required';
  }

  if (values.accountType === 'pws' || values.accountType === AccountType.PWS) {
    if (isEmptyString(values.pwsid)) {
      errors.pwsid = 'PWSID is Required';
    } else if (
      typeof values.pwsid === 'string' &&
      (values.pwsid.trim().length !== 9 ||
        !SDWIS_ID_REGEX.test(values.pwsid.trim()))
    ) {
      errors.pwsid =
        'PWSID incorrectly formatted, examples: IN1234567, UTAH12345, WA123456V';
    }
  }

  if (
    (values.accountType === 'state' ||
      values.accountType === AccountType.StateDashboard) &&
    isEmptyString(values.primacyCode)
  ) {
    errors.primacyCode = 'Primacy Code is Required';
  }

  return errors;
};

export const sanitizeAccount = (values: Values): Partial<Values> => {
  const clonedValues = omit(values, ['__typename']);

  if (clonedValues.pwsid) {
    clonedValues.pwsid = clonedValues.pwsid.trim();
  }
  if (!clonedValues.isDemo) {
    clonedValues.isDemo = false;
  }

  if (clonedValues.contactPhone !== undefined) {
    clonedValues.contactPhone = clonedValues.contactPhone?.replace(/\D/g, '');
  }

  if (!clonedValues.streetAddressLine2) {
    clonedValues.streetAddressLine2 = null;
  }

  if (isEmptyString(clonedValues.subdomain || '')) {
    clonedValues.subdomain = null;
  }

  if (clonedValues.pwsid) {
    let primacyCode = clonedValues.pwsid.replace(/\d/g, '');
    if (!/[a-zA-Z]/.test(primacyCode)) {
      primacyCode = clonedValues.pwsid.slice(0, 2);
    }
    clonedValues.primacyCode = primacyCode;
  }

  if (clonedValues.primacyCode && clonedValues.primacyCode?.length > 4) {
    clonedValues.primacyCode = clonedValues.primacyCode
      .replace(/\d/g, '')
      .slice(0, 4);
  }

  if (clonedValues.populationServedCount) {
    clonedValues.populationServedCount = parseInt(
      `${clonedValues.populationServedCount}`,
      10
    );
  }

  if (clonedValues.serviceLineConnectionsCount) {
    clonedValues.serviceLineConnectionsCount = parseInt(
      `${clonedValues.serviceLineConnectionsCount}`,
      10
    );
  }

  if (clonedValues.latitude) {
    clonedValues.latitude = parseFloat(`${clonedValues.latitude}`);
  }

  if (clonedValues.longitude) {
    clonedValues.longitude = parseFloat(`${clonedValues.longitude}`);
  }

  switch (clonedValues.accountType) {
    case 'pws':
      clonedValues.accountType = AccountType.PWS;
      break;
    case 'schools':
      clonedValues.accountType = AccountType.Schools;
      break;
    case 'state':
      clonedValues.accountType = AccountType.StateDashboard;
      break;
    case 'hydra':
      clonedValues.accountType = AccountType.Hydra;
      break;
    default:
      clonedValues.accountType = AccountType.Unknown;
      break;
  }

  return clonedValues;
};

export const AccountForm = (): JSX.Element => {
  const { errors = {}, touched = {} } = useFormState();
  const {
    input: { value: accountType }
  } = useField('accountType');
  const isState =
    accountType === 'state' || accountType === AccountType.StateDashboard;
  const isHydra = accountType === 'hydra' || accountType === AccountType.Hydra;
  const isBackOffice =
    accountType === 'backoffice' || accountType === AccountType.Unknown;
  return (
    <Form>
      <Form.Group widths="equal">
        <Form.Field required>
          <Field<string> name="name">
            {({ input, meta }) => (
              <>
                <TextField input={input} meta={meta} label="Name *" />
                {meta.touched && meta.error && (
                  <Label basic color="red" pointing>
                    {meta.error}
                  </Label>
                )}
              </>
            )}
          </Field>
        </Form.Field>
        {!isBackOffice && (
          <Form.Field required>
            <Field<string> name="companyName">
              {({ input, meta }) => (
                <>
                  <TextField input={input} meta={meta} label="Company Name *" />
                  {meta.touched && meta.error && (
                    <Label basic color="red" pointing>
                      {meta.error}
                    </Label>
                  )}
                </>
              )}
            </Field>
          </Form.Field>
        )}
        {isState && (
          <Form.Field>
            <Field<string> name="primacyCode">
              {({ input, meta }) => (
                <>
                  <TextField
                    input={input}
                    meta={meta}
                    label="Primacy Agency Code *"
                  />
                  {meta.touched && meta.error && (
                    <Label basic color="red" pointing>
                      {meta.error}
                    </Label>
                  )}
                </>
              )}
            </Field>
          </Form.Field>
        )}
      </Form.Group>
      {!isState && !isHydra && !isBackOffice && (
        <Form.Group widths="equal">
          <SubdomainInput />
        </Form.Group>
      )}
      <Divider section />
      {!isBackOffice && (
        <>
          <Form.Group>
            <Form.Field style={{ width: '60%' }}>
              <Field<string> name="streetAddressLine1">
                {({ input, meta }) => (
                  <>
                    <TextField
                      input={input}
                      meta={meta}
                      label="Address Line 1 *"
                    />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
            <Form.Field style={{ width: '40%' }}>
              <Field<string> name="city">
                {({ input, meta }) => (
                  <>
                    <TextField input={input} meta={meta} label="City *" />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field style={{ width: '40%' }}>
              <Field<string> name="streetAddressLine2">
                {({ input, meta }) => (
                  <>
                    <TextField
                      input={input}
                      meta={meta}
                      label="Address Line 2"
                    />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
            <Form.Field style={{ width: '35%' }}>
              <Field<string> name="state">
                {({ input, meta }) => (
                  <>
                    <Dropdown
                      input={input}
                      meta={meta}
                      fluid
                      label="State *"
                      options={StateList}
                    />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
            <Form.Field style={{ width: '25%' }}>
              <Field<string> name="zip">
                {({ input, meta }) => (
                  <>
                    <TextField input={input} meta={meta} label="ZIP *" />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Field>
              <Field<string> name="latitude">
                {({ input, meta }) => (
                  <>
                    <TextField input={input} meta={meta} label="Latitude" />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
            <Form.Field>
              <Field<string> name="longitude">
                {({ input, meta }) => (
                  <>
                    <TextField input={input} meta={meta} label="Longitude" />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
          </Form.Group>
          <Divider section />
          <Form.Group widths="equal">
            <Form.Field>
              <Field<string> name="contactName">
                {({ input, meta }) => (
                  <>
                    <TextField
                      input={input}
                      meta={meta}
                      label="Contact Name *"
                    />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
            <Form.Field>
              <Field<string> name="contactPhone">
                {({ input, meta }) => {
                  // Show error if either phone or email is required and both are empty
                  const showEitherError =
                    (meta.touched || touched.contactEmail) &&
                    errors.contactEmail &&
                    !input.value;
                  return (
                    <>
                      <TextField
                        input={input}
                        meta={meta}
                        label="Contact Phone *"
                      />
                      {(meta.touched && meta.error) || showEitherError ? (
                        <Label basic color="red" pointing>
                          {meta.error || errors.contactEmail}
                        </Label>
                      ) : null}
                    </>
                  );
                }}
              </Field>
            </Form.Field>
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Field>
              <Field<string> name="contactEmail">
                {({ input, meta }) => {
                  // Show error if either phone or email is required and both are empty
                  const showEitherError =
                    (meta.touched || touched.contactPhone) &&
                    errors.contactPhone &&
                    !input.value;
                  return (
                    <>
                      <TextField
                        input={input}
                        meta={meta}
                        label="Contact Email *"
                      />
                      {(meta.touched && meta.error) || showEitherError ? (
                        <Label basic color="red" pointing>
                          {meta.error || errors.contactPhone}
                        </Label>
                      ) : null}
                    </>
                  );
                }}
              </Field>
            </Form.Field>
          </Form.Group>
          <Divider section />
          <Form.Group widths="equal">
            <Form.Field>
              <Field<string> name="connection">
                {({ input, meta }) => (
                  <>
                    <TextField
                      input={input}
                      meta={meta}
                      label="Auth0 Connection"
                    />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
            <Form.Field>
              <Field<string> name="timezone">
                {({ input, meta }) => (
                  <>
                    <Dropdown
                      input={input}
                      meta={meta}
                      fluid
                      label="Timezone Setting"
                      options={timezoneOptions}
                    />
                    {meta.touched && meta.error && (
                      <Label basic color="red" pointing>
                        {meta.error}
                      </Label>
                    )}
                  </>
                )}
              </Field>
            </Form.Field>
          </Form.Group>
        </>
      )}
      {(accountType === 'pws' || accountType === AccountType.PWS) && (
        <PwsInfo />
      )}
    </Form>
  );
};

const SubdomainInput = (): JSX.Element => {
  const {
    input: { value: accountType }
  } = useField('accountType');
  const isSchools =
    accountType === 'schools' || accountType === AccountType.Schools;
  const labelPosition = isSchools ? 'right' : 'left';

  return (
    <Field<string> name="subdomain">
      {({ input, meta }) => {
        const subdomainErrorStyling =
          input.value && !isValidSubdomain(input.value)
            ? { borderColor: 'red', borderWidth: '2px' }
            : {};
        const content = isSchools ? (
          <>
            <Label>https://</Label>
            <input
              style={subdomainErrorStyling}
              onChange={evt => input.onChange(evt.target.value)}
              value={input.value || ''}
            />
            <Label>.120wateraudit.com</Label>
          </>
        ) : (
          <>
            <Label>https://pws-ptd.120wateraudit.com/</Label>
            <input
              style={subdomainErrorStyling}
              onChange={evt => input.onChange(evt.target.value)}
              value={input.value || ''}
            />
          </>
        );
        return (
          <Form.Field>
            <label>
              PTD URL ( Note: The PTD URL for a School Account cannot be updated
              after initially set. Please get technical assistance if this is
              needed)
            </label>
            <Input labelPosition={labelPosition} type="text">
              {content}
            </Input>
            {meta.touched && meta.error && (
              <Label basic color="red" pointing>
                {meta.error}
              </Label>
            )}
          </Form.Field>
        );
      }}
    </Field>
  );
};

const PwsInfo = (): JSX.Element => {
  return (
    <>
      <Divider section />
      <PWSIDHelpText>
        <p>
          For PWS Accounts we require the PWSID below to be blank or to provide
          the SDWIS ID.{` `}
          <a href="https://www.epa.gov/enviro/sdwis-search" target="_blank">
            You can help locate the correct ID here if desired.
          </a>
        </p>
        <a href="https://www.epa.gov/enviro/sdwis-search" target="_blank">
          <Icon name="question circle outline" />
        </a>
      </PWSIDHelpText>
      <Form.Group widths="equal">
        <Form.Field>
          <Field<string> name="pwsid">
            {({ input, meta }) => (
              <>
                <TextField input={input} meta={meta} label="PWSID*" />
                {meta.touched && meta.error && (
                  <Label basic color="red" pointing>
                    {meta.error}
                  </Label>
                )}
              </>
            )}
          </Field>
        </Form.Field>
        <Form.Field>
          <Field<string> name="pwsType">
            {({ input, meta }) => (
              <>
                <Dropdown
                  input={input}
                  meta={meta}
                  fluid
                  label="PWS Type"
                  options={pwsTypeOptions}
                />
                {meta.touched && meta.error && (
                  <Label basic color="red" pointing>
                    {meta.error}
                  </Label>
                )}
              </>
            )}
          </Field>
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field>
          <Field<string> name="sdwisContact">
            {({ input, meta }) => (
              <>
                <TextField input={input} meta={meta} label="SDWIS Contact" />
                {meta.touched && meta.error && (
                  <Label basic color="red" pointing>
                    {meta.error}
                  </Label>
                )}
              </>
            )}
          </Field>
        </Form.Field>
        <Form.Field>
          <Field<string> name="owner">
            {({ input, meta }) => (
              <>
                <Dropdown
                  input={input}
                  meta={meta}
                  fluid
                  label="PWS Owner"
                  options={ownerOptions}
                />
                {meta.touched && meta.error && (
                  <Label basic color="red" pointing>
                    {meta.error}
                  </Label>
                )}
              </>
            )}
          </Field>
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field>
          <Field<string> name="serviceLineConnectionsCount">
            {({ input, meta }) => (
              <>
                <TextField
                  input={input}
                  meta={meta}
                  label="Service Line Connections"
                  min="0"
                  step="1"
                  type="number"
                />
                {meta.touched && meta.error && (
                  <Label basic color="red" pointing>
                    {meta.error}
                  </Label>
                )}
              </>
            )}
          </Field>
        </Form.Field>
        <Form.Field>
          <Field<string> name="populationServedCount">
            {({ input, meta }) => (
              <>
                <TextField
                  input={input}
                  meta={meta}
                  label="Population Served"
                  min="0"
                  step="1"
                  type="number"
                />
                {meta.touched && meta.error && (
                  <Label basic color="red" pointing>
                    {meta.error}
                  </Label>
                )}
              </>
            )}
          </Field>
        </Form.Field>
      </Form.Group>
    </>
  );
};

const PWSIDHelpText = styled.div`
  opacity: 0.5;
  font-weight: 800 !important;
  font-size: 2rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
`;
